import React, { ReactNode, createContext, useContext, useState } from 'react'

/**
 * Interface for the Embed context
 */
interface EmbedContextType {
    isEmbed: boolean
    showHamburgerMenu: boolean
    setIsEmbed: (value: boolean) => void
    setShowHamburgerMenu: (value: boolean) => void
    applyPrimaryColorToButton: (element: HTMLButtonElement) => void
    primaryColor?: string
    setPrimaryColor: (color: string | undefined) => void
}

// Create the context with default values
// Default function definitions which use the parameters are used to avoid linting errors
const EmbedContext = createContext<EmbedContextType>({
    isEmbed: false,
    showHamburgerMenu: false,
    setIsEmbed: _value => {
        _value
    },
    setShowHamburgerMenu: _value => {
        _value
    },
    applyPrimaryColorToButton: _element => {
        _element
    },
    primaryColor: undefined,
    setPrimaryColor: _color => {
        _color
    },
})

/**
 * Hook for accessing the Embed context
 */
export const useEmbed = () => useContext(EmbedContext)

interface EmbedProviderProps {
    children: ReactNode
}

/**
 * Provider component for the Embed context
 */
export const EmbedProvider: React.FC<EmbedProviderProps> = ({ children }) => {
    const [isEmbed, setIsEmbed] = useState(false)
    const [showHamburgerMenu, setShowHamburgerMenu] = useState(false)
    const [primaryColor, setPrimaryColor] = useState<string | undefined>(undefined)

    // Apply primary color directly to a button element
    const applyPrimaryColorToButton = (element: HTMLButtonElement) => {
        if (!primaryColor || !element) return

        // Add primary color styling
        element.style.backgroundColor = primaryColor
        element.style.color = 'white'

        // Add hover effect
        const originalBackground = element.style.backgroundColor

        element.addEventListener('mouseenter', () => {
            element.style.filter = 'brightness(1.1)'
        })

        element.addEventListener('mouseleave', () => {
            element.style.filter = ''
            element.style.backgroundColor = originalBackground
        })

        // Handle disabled state
        if (element.disabled) {
            const hexWithOpacity = `${primaryColor}40` // 40 = 25% opacity in hex
            element.style.backgroundColor = hexWithOpacity
            element.style.opacity = '0.7'
            element.style.cursor = 'not-allowed'
            element.style.color = 'rgba(255, 255, 255, 0.7)'
        }
    }

    return (
        <EmbedContext.Provider
            value={{
                isEmbed,
                showHamburgerMenu,
                setIsEmbed,
                setShowHamburgerMenu,
                applyPrimaryColorToButton,
                primaryColor,
                setPrimaryColor,
            }}
        >
            {children}
        </EmbedContext.Provider>
    )
}

export default EmbedProvider
